import type {IntlShape} from 'react-intl';
import {CompareSummary, compareFigures, delta, formatMoneyCents} from './baselineVersionsMath';

export interface SummaryOrigin {
	readonly source: string | null;
	readonly scaleDirection: string | null;
	readonly scalePercent: number | null;
	readonly templateName: string | null;
	readonly templateDescription: string | null;
}

export interface SummaryPhase {
	readonly name: string | null;
	readonly minutes: number | null;
	readonly roleCount: number | null;
}

export interface SummaryVersion {
	readonly isStandard: boolean | null;
	readonly summary: CompareSummary | null;
	readonly origin: SummaryOrigin | null;
	readonly phaseTotals: ReadonlyArray<SummaryPhase> | null;
}

interface Options {
	formatMessage: IntlShape['formatMessage'];
	currencySymbol: string;
	seesCost: boolean;
}

const hours = (p: SummaryPhase) => Math.round((p.minutes ?? 0) / 60);

/**
 * Each phase keyed by its trimmed, case-folded name plus how many earlier phases share that name, so phases with the
 * same name pair up in order (the first "Design" with the first "Design") instead of all matching one of them.
 */
const keyed = (phases: ReadonlyArray<SummaryPhase>): Array<[string, SummaryPhase]> => {
	const seen = new Map<string, number>();
	return phases.map(p => {
		const name = (p.name ?? '').trim().toLowerCase();
		const n = seen.get(name) ?? 0;
		seen.set(name, n + 1);
		return [`${n}:${name}`, p];
	});
};
const EMPTY: CompareSummary = {revenue: 0, cost: null, profit: null, margin: null, minutes: 0};

/**
 * How a version differs from Standard, in plain words. Phases match by trimmed name ignoring case, and same-named
 * phases in order; hours compare as whole hours; lists join with "and" only, as the design does.
 */
export const buildVersionSummary = (standard: SummaryVersion, version: SummaryVersion, opts: Options): string => {
	const {formatMessage, currencySymbol, seesCost} = opts;
	const t = (id: string, values?: Record<string, string | number>) => formatMessage({id}, values);
	if (version.isStandard) {
		return t('baseline.versions.summary.reference');
	}
	const origin = version.origin;
	if (origin?.source === 'TEMPLATE' && origin.templateDescription?.trim()) {
		return origin.templateDescription.trim();
	}

	const parts: string[] = [];
	if (origin?.source === 'COPY_SCALED' && origin.scalePercent != null) {
		parts.push(
			t('baseline.versions.summary.origin_scaled', {
				percent: origin.scalePercent,
				direction: origin.scaleDirection ?? 'MORE',
			})
		);
	} else if (origin?.source === 'TEMPLATE' && origin.templateName) {
		parts.push(t('baseline.versions.summary.origin_template', {name: origin.templateName}));
	} else if (origin?.source === 'COPY') {
		parts.push(t('baseline.versions.summary.origin_copy'));
	}

	const a = compareFigures(standard.summary ?? EMPTY);
	const b = compareFigures(version.summary ?? EMPTY);
	const dRevenue = delta('revenue', b, a) ?? 0;
	const dHours = delta('hours', b, a) ?? 0;
	const dMargin = seesCost ? delta('margin', b, a) ?? 0 : 0;

	const stdPhases = standard.phaseTotals ?? [];
	const verPhases = version.phaseTotals ?? [];
	const stdKeyed = keyed(stdPhases);
	const verKeyed = keyed(verPhases);
	const stdByKey = new Map(stdKeyed);
	const verKeys = new Set(verKeyed.map(([k]) => k));
	const added = verKeyed.filter(([k]) => !stdByKey.has(k)).map(([, p]) => p);
	const removed = stdKeyed.filter(([k]) => !verKeys.has(k)).map(([, p]) => p);
	// Each version phase with the Standard phase it pairs with.
	const shared = verKeyed.flatMap(([k, p]) => {
		const s = stdByKey.get(k);
		return s ? [{phase: p, std: s}] : [];
	});
	const hoursChanged = shared.filter(({phase, std}) => hours(phase) !== hours(std));
	const rolesChanged = shared.some(({phase, std}) => (phase.roleCount ?? 0) !== (std.roleCount ?? 0));

	const differs =
		dRevenue !== 0 || dHours !== 0 || dMargin !== 0 || added.length > 0 || removed.length > 0 || hoursChanged.length > 0;
	if (!differs) {
		// Only role counts differ: the opening line alone. Nothing differs at all: say so.
		if (!rolesChanged) {
			parts.push(t('baseline.versions.summary.no_changes'));
		}
		return parts.join(' ');
	}

	const totals: string[] = [];
	if (dRevenue) {
		const amount = formatMoneyCents(Math.abs(dRevenue), currencySymbol);
		totals.push(
			t(dRevenue > 0 ? 'baseline.versions.summary.revenue_higher' : 'baseline.versions.summary.revenue_lower', {amount})
		);
	}
	if (dHours) {
		totals.push(
			t(dHours > 0 ? 'baseline.versions.summary.hours_more' : 'baseline.versions.summary.hours_fewer', {
				hours: Math.abs(dHours),
			})
		);
	}
	if (dMargin) {
		totals.push(
			t(dMargin > 0 ? 'baseline.versions.summary.margin_up' : 'baseline.versions.summary.margin_down', {
				points: Math.abs(dMargin),
			})
		);
	}
	if (totals.length) {
		const joined = totals.join(t('baseline.versions.summary.totals_separator'));
		parts.push(t('baseline.versions.summary.totals', {totals: joined.charAt(0).toLocaleUpperCase() + joined.slice(1)}));
	}

	const and = ` ${t('baseline.versions.summary.and')} `;
	const names = (list: ReadonlyArray<SummaryPhase>) => list.map(p => (p.name ?? '').trim()).join(and);
	if (shared.length === 0) {
		parts.push(
			t('baseline.versions.summary.different_structure', {count: verPhases.length, standardCount: stdPhases.length})
		);
	} else {
		if (hoursChanged.length) {
			const items = hoursChanged.map(({phase, std}) =>
				t('baseline.versions.summary.phase_hours', {
					name: (phase.name ?? '').trim(),
					from: hours(std),
					to: hours(phase),
				})
			);
			parts.push(t('baseline.versions.summary.hours_changed', {phases: items.join(and)}));
		}
		if (added.length) {
			parts.push(t('baseline.versions.summary.adds', {phases: names(added)}));
		}
		if (removed.length) {
			parts.push(t('baseline.versions.summary.removes', {phases: names(removed)}));
		}
	}
	return parts.join(' ');
};
